import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import useSeo from "../hooks/useSeo";
import GripSystem from "../components/GripSystem";

const GripsPage = () => {
  const [grips, setGrips] = useState([]);

  useEffect(() => {
    api.get("/grips").then((r) => setGrips(r.data)).catch((err) => {
      console.warn("[grips] failed to load", err);
    });
  }, []);

  useSeo({
    title: "The Grip System · 8 Football-Specific Grip Patterns",
    description:
      "Pritch grip patterns are engineered around how footballers actually move. Eight grip designs — Grid, Flow, Vector, Honeycomb, Origin, Wave, Ascent, Atlas — built for sprint, cut, stop, strike.",
    path: "/grips",
    jsonLdId: "grips-list-jsonld",
    jsonLd:
      grips.length > 0
        ? {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "The Pritch Grip System",
            description: "Eight football-specific grip patterns engineered for matchday performance.",
            itemListOrder: "https://schema.org/ItemListOrderAscending",
            numberOfItems: grips.length,
            itemListElement: grips.map((g, idx) => ({
              "@type": "ListItem",
              position: idx + 1,
              name: g.name,
              url: `https://pritch.fc/grips/${g.slug}`,
              description: `${g.tagline} ${g.subline}`,
            })),
          }
        : undefined,
  });

  return (
    <div data-testid="grips-page">
      <section className="border-b border-line bg-chalk">
        <div className="container-x py-16 md:py-24">
          <p className="overline mb-4">Performance, From The Ground Up</p>
          <h1 className="font-display uppercase text-5xl md:text-7xl tracking-tight font-bold leading-[0.95]">
            The Grip
            <br />
            <span className="text-mute">System.</span>
          </h1>
          <p className="mt-6 max-w-xl text-mute leading-relaxed">
            Eight football-specific grip patterns — each engineered for a different way you move on
            the pitch. Pick the one that matches your game, or rotate them across training, matchday
            and recovery.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/shop" className="btn-primary" data-testid="grips-shop-cta">
              Shop the range <ArrowRight size={14} className="ml-2" />
            </Link>
            <a href="#grip-grid" className="btn-secondary">
              Browse grips
            </a>
          </div>
        </div>
      </section>

      <GripSystem grips={grips} />

      <section className="container-x py-16 md:py-24" data-testid="grips-deep-list">
        <p className="overline mb-3">Browse · Deep dive</p>
        <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight font-bold leading-[0.95] mb-10">
          One grip per scenario.
        </h2>
        <ul className="divide-y divide-line border-y border-line">
          {grips.map((g) => (
            <li
              key={g.slug}
              id={`detail-${g.slug}`}
              className="py-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-center"
              data-testid={`grip-detail-row-${g.slug}`}
            >
              <div className="md:col-span-1">
                <p className="mono text-xs text-mute">{g.no}</p>
              </div>
              <div className="md:col-span-4">
                <h3 className="font-display uppercase text-2xl tracking-tight font-bold leading-[1]">
                  {g.name}
                </h3>
                <p className="mt-2 text-sm">
                  <span className="font-medium">{g.tagline}</span>{" "}
                  <span className="text-mute">{g.subline}</span>
                </p>
              </div>
              <div className="md:col-span-5">
                <p className="text-sm text-mute leading-relaxed">{g.description}</p>
              </div>
              <div className="md:col-span-2 md:text-right">
                <Link
                  to={`/grips/${g.slug}`}
                  className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase font-bold hover:gap-3 transition-all"
                  data-testid={`grip-detail-cta-${g.slug}`}
                >
                  Explore <ArrowRight size={14} />
                </Link>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};

export default GripsPage;
